/**
 * @module main
 * @description Entry point da aplicação GVI (ES Modules).
 * Orquestra inicialização: imports → bridge global → event-delegation → landing.
 * 
 * Este é o ÚNICO entry point para o esbuild. Todos os módulos são importados
 * aqui e o esbuild resolve o grafo de dependências automaticamente.
 */

// === Layer 0: Event bus (must be first — sets up window.__GVI_BUS) ===
import { setState } from './event-bus.js';

// === Layer 0.5: Side-effects (auto-executa) ===
import { normalizarTexto } from './platform-detect.js';

// === Layer 1: Core state & utilities ===
import {
    GVI_CONFIG, GVI_STATE, GVI_GEO, GVI_MAP,
    escapeHTML, assignSafeHTML, obterCaminhoBase, obterCaminhoFoto, obterSecKey,
    extrairNumeroSecao, CORES_COMPARACOES, obterFotoDemo,
    getAnoState, getOposicaoPrefix, getExcludedVereador,
    isOposicao, isExcludedVereador, isEleicaoGovernador,
    getCargoExecutivoLabel, getCargoLegislativoLabel, temCargoLegislativo,
    getEmojiExecutivo, getEmojiLegislativo, MUNICIPIO_UF
} from './config.js';

// === Layer 2: Auth ===
import { GVI_AUTH } from './auth.js';

// === Layer 3: Data loading ===
import { carregarGeoLayout, isPointInPolygon } from './data.js';

// === Layer 4: Geo computation ===
import {
    obterCorCandidato, obterVencedorInfo, obterCorDesempenho,
    contarVotosCandidatoEmSecoes, obterTopCandidatosPorSecoes,
    contarVotosCamaraoEmSecoes, _obterCorPartidoPorNumero
} from './geo.js';

// === Layer 5: Map core ===
import {
    inicializarMapa, desenharAlfinetes, atualizarVisibilidadePins,
    ajustarBolinhas, redesenharLimiteMunicipio, fecharModal, abrirModal
} from './map-core.js';

// === Layer 6: Heatmap ===
import {
    alternarModoVisualizacao, alternarModoMaisVotados,
    atualizarMapaMaisVotados, atualizarHeatmap, alternarModoComparativo
} from './heatmap.js';

// === Layer 7: System init ===
import { iniciarSistema, voltarHome } from './system-init.js';

// === Layer 8: Print & welcome ===
import { imprimirGuiaAtiva, imprimirAnotacoes, mostrarBoasVindas, fecharBoasVindas } from './print.js';

// === Layer 9: Candidate selection ===
import {
    mudarAba, toggleDrawer, initDrawerVerticalDragging,
    atualizarMapaPorCandidato, selecionarCamaraoMapa,
    selecionarCandidatoIndividual, selecionarCandidatoComparacao,
    atualizarMapaComparativo, atualizarMapaPorCamarao, initDragScroll
} from './candidate-selection.js';

// --- Fim dos imports principais ---

// === Layer 15: Location editor (ESM export) ===
import { GVI_LOCAIS } from './location-editor.js';

// === Layer 15.5: Map drift (ESM — previously standalone IIFE) ===
import { GVI_DRIFT } from './map-drift.js';

// === Layer 16: Profile (ESM export) ===
import { GVI_PROFILE } from './profile.js';

// === Layer 17: Event delegation ===
import { GVI_registerClickActions, GVI_registerChangeActions, GVI_registerInputActions } from './event-delegation.js';

// === Layer 18: Landing page ===
import { mostrarEtapa } from './landing.js';

// ==========================================
// EVENT BUS: Registra estado compartilhado
// ==========================================
// O event bus (window.__GVI_BUS) é o ponto de contato entre o
// bundle ESM e scripts externos (demo-init.js, preview.js, sobre.js).
setState('config', GVI_CONFIG);
setState('state', GVI_STATE);
setState('geo', GVI_GEO);
setState('map', GVI_MAP);
setState('auth', GVI_AUTH);
setState('normalizarTexto', normalizarTexto);

// ==========================================
// BRIDGE MÍNIMO: Apenas o essencial para scripts fora do bundle
// ==========================================
// demo-init.js (carregado separadamente em demo.html) precisa de:
//   - window.GVI_CONFIG, GVI_STATE, GVI_GEO, GVI_MAP, GVI_AUTH (estado)
//   - window.assignSafeHTML, iniciarSistema (funções)
//   - window.MUNICIPIO_UF (mapa de dados)
// NOTA: GVI_registerClickActions etc. já são expostos em event-delegation.js.
// NOTA: GVI_LOCAIS, GVI_PROFILE, GVI_DRIFT já são expostos nos seus módulos.
Object.assign(window, {
    // State namespaces (para demo-init.js e depuração no DevTools)
    GVI_CONFIG, GVI_STATE, GVI_GEO, GVI_MAP, GVI_AUTH,
    MUNICIPIO_UF, CORES_COMPARACOES,

    // Funções essenciais para demo-init.js
    escapeHTML,
    assignSafeHTML,
    iniciarSistema,
    abrirModal,
    normalizarTexto,
});
